import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { ProductViewBase, ProductPreview } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { useBundle } from '@amzn/react-arb-tools';
import {
  ASIN_VARIANT,
  AdvocatesComponent,
  CARD_SIZE,
  Card,
  CTA_TYPE_MIGRATED,
  DynamicList,
  DYNAMIC_LIST_SVG_SCALE,
  DYNAMIC_LIST_SUBTITLE_SPACING,
  DYNAMIC_LIST_TITLE_SPACING,
  HEADING_TAG,
  ICON_NAME,
  ListComponentMigrated,
  Loader,
  LOADER_SIZE,
  MainBanner,
  OFFER_TIER,
  Outro,
  PageSection,
  PRIMARY_BUTTON_VARIANT,
  PRODUCT_VARIANT,
  SECTION_THEME,
  SIMPLE_ACCORDION_VARIANT,
  Slider,
  StorefrontDynamicListSVG,
  StorefrontHero,
  TAGLINE_COLORS,
  TextComponent,
  getAsin,
  useDeviceSize,
} from '@amzn/sss-website-theme';

import { useSssApiCreditsClient } from 'src/clients';
import { isWeblabSegmentPagesEnabled } from 'src/clients/weblab/weblab-client';
import { CRL_DOMAIN, ENV_STAGE } from 'src/constants/env-constants';
import {
  COMMON_BUNDLE_NAME,
  SEGMENT_PAGE_BUNDLE_NAME,
  STORE_PAGE_BUNDLE_NAME,
} from 'src/constants/panther/resource-bundles';
import { OFFER_TIER_BADGE_KEY } from 'src/enums/OfferTierBadgeCopy';
import { ROUTES } from 'src/enums/Routes';
import { getCurrencySymbol } from 'src/helpers';
import { getCreditsAssetRoot, MEDIA_TYPE } from 'src/helpers/resource-library/library-helpers';
import { getSegmentRoute } from 'src/helpers/segments/segment-route-helpers';
import { t } from 'src/helpers/translations/translation-helpers';
import { useGetUserPreferences } from 'src/hooks';
import { useGetUserRights } from 'src/hooks/useGetUserRights.hook';
import {
  CREDIT_CATEGORY_TAG_CONFIG,
  CREDIT_TYPE_TAG_CONFIG,
  CREDIT_SEGMENT_TAG_CONFIG,
} from 'src/hooks/useProductTags';
import { useScrollPauseFadeOut } from 'src/hooks/useScrollPauseFadeOut';

import styles from './StorePage.module.scss';

const STORE_PRODUCTS = [
  PRODUCT_VARIANT.COTE_DIVOIRE_2023,
  PRODUCT_VARIANT.REFRIGERANT,
  PRODUCT_VARIANT.BIO_DIESEL_2024,
  PRODUCT_VARIANT.BIO_DIESEL_2025,
  PRODUCT_VARIANT.RENEWABLE_DIESEL,
  PRODUCT_VARIANT.EU_LCF_2024,
  PRODUCT_VARIANT.EU_LCF_2025,
  PRODUCT_VARIANT.MARITIME_FUELS_2025,
];

export const StorePage = () => {
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [storeBundle] = useBundle(STORE_PAGE_BUNDLE_NAME);
  const [segmentPageBundle] = useBundle(SEGMENT_PAGE_BUNDLE_NAME);

  const [products, setProducts] = useState<Array<ProductViewBase | ProductPreview>>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  const { isSmallDesktop } = useDeviceSize();
  const isSmallDevice = useMediaQuery({ query: '(max-width: 600px)' });
  const { isAuthenticated, userRights, isLoading: isLoadingRights } = useGetUserRights();
  const isApproved = isAuthenticated && userRights.includes(UserRight.KATHARINE_APPROVED);
  const isPrescreened = isAuthenticated && userRights.includes(UserRight.KATHARINE_PRESCREENED);
  const creditsClient = useSssApiCreditsClient();
  const { userPreferredMarketplaceId, isLoading: isLoadingPreferences } = useGetUserPreferences();

  const { ref: dynamicListSectionRef, isFadingOut, hasFadedOut } = useScrollPauseFadeOut({ lockDuration: 600 });

  useEffect(() => {
    if (isLoadingPreferences || isLoadingRights) return;

    const fetchProducts = async () => {
      try {
        const stage = ENV_STAGE.toUpperCase() === '{{STAGE}}' ? 'BETA' : ENV_STAGE;
        const fetchFn = isApproved ? creditsClient.getEacProduct : creditsClient.getEacProductPreview;
        const results = await Promise.allSettled(
          STORE_PRODUCTS.map(async (variant) => {
            const asin = getAsin(variant, ASIN_VARIANT.BUNDLED, stage, userPreferredMarketplaceId);
            const response = await fetchFn(asin);
            return response.product ?? null;
          })
        );
        setProducts(
          results
            .filter((r) => r.status === 'fulfilled' && r.value !== null)
            .map((r) => (r as PromiseFulfilledResult<ProductViewBase | ProductPreview>).value)
        );
      } catch {
        // silently fail - products that don't exist in this stage are filtered out
      } finally {
        setIsLoadingProducts(false);
      }
    };

    if (isApproved || isPrescreened) {
      fetchProducts();
    } else {
      setIsLoadingProducts(false);
    }
  }, [creditsClient, userPreferredMarketplaceId, isLoadingPreferences, isLoadingRights, isApproved, isPrescreened]);

  if (!storeBundle || !commonBundle || !segmentPageBundle) {
    return null;
  }

  return (
    <div className={styles.storefront}>
      {/* Hero */}
      <StorefrontHero
        heading={t(storeBundle, 'hero_heading')}
        copy={t(storeBundle, 'hero_description')}
        backgroundImage={{
          src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-1.jpeg`,
        }}
      />

      {/* Available credits */}
      {isLoadingProducts && (
        <PageSection theme="squid-ink">
          <Loader size={LOADER_SIZE.LARGE} theme={SECTION_THEME.Dark} />
        </PageSection>
      )}
      {products.length > 0 && (
        <PageSection className={styles.availableCredits} id="available-credits" theme="squid-ink">
          <ListComponentMigrated
            className={styles.list}
            hasMobileImages
            isRebrandingActive
            theme={SECTION_THEME.Dark}
            items={products.map((prod) => {
              const segmentRoute = getSegmentRoute(prod.productCreditSegment);
              let pricing;
              let label;

              if (isApproved) {
                if ('productMatchedOfferPrice' in prod && prod.productMatchedOfferPrice != null) {
                  const matchedPrice = prod.productMatchedOfferPrice;

                  const basePrice =
                    'productBaseOfferPrice' in prod ? (prod as ProductViewBase).productBaseOfferPrice : undefined;

                  const matchedTier =
                    'productMatchedOfferTier' in prod ? (prod as ProductViewBase).productMatchedOfferTier : undefined;
                  let strikeThroughProps = {};
                  if (matchedTier && matchedTier !== OFFER_TIER.BASE && basePrice != null && basePrice > matchedPrice) {
                    const badgeKey = OFFER_TIER_BADGE_KEY[matchedTier] ?? 'product_signatory_price_tag';
                    strikeThroughProps = {
                      originalPrice: basePrice,
                      badgeCopy: t(storeBundle, badgeKey),
                    };
                  }

                  pricing = {
                    price: matchedPrice,
                    currency: getCurrencySymbol(prod.productOfferCurrency),
                    priceLabel: t(storeBundle, 'product_price_suffix'),
                    ...strikeThroughProps,
                  };
                } else {
                  label = t(storeBundle, 'product_cta_see_details');
                }
              } else if (isPrescreened && segmentRoute != null) {
                label = t(storeBundle, 'product_learn_more');
              }

              return {
                title: prod.productTitle ?? '',
                subtitle: prod.productDescription ?? '',
                image: {
                  src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/${prod.productDetailAtfHeroImage}`,
                  alt: prod.productTitle ?? '',
                },
                cta: (() => {
                  const ariaLabelCopy = {
                    exploreText: t(commonBundle, 'newTab_label'),
                    newTabText: t(commonBundle, 'newTab_label'),
                    pdfText: '',
                  };
                  if (isApproved) {
                    return {
                      href: `/credits/product/${encodeURIComponent(prod.asin)}`,
                      type: CTA_TYPE_MIGRATED.Link,
                      icon: ICON_NAME.CHEVRON_RIGHT,
                      forceServerSideNav: true,
                      ariaLabelCopy,
                    };
                  }
                  if (isPrescreened && segmentRoute) {
                    return {
                      href: segmentRoute,
                      type: CTA_TYPE_MIGRATED.Link,
                      icon: ICON_NAME.CHEVRON_RIGHT,
                      forceServerSideNav: true,
                      ariaLabelCopy,
                    };
                  }
                  return undefined;
                })(),
                pricing,
                label,
                theme: SECTION_THEME.Dark,
                taglines: [
                  prod.productCreditCategory && CREDIT_CATEGORY_TAG_CONFIG[prod.productCreditCategory]
                    ? {
                        copy: t(commonBundle, CREDIT_CATEGORY_TAG_CONFIG[prod.productCreditCategory].stringId),
                        icon: CREDIT_CATEGORY_TAG_CONFIG[prod.productCreditCategory].icon,
                        color: TAGLINE_COLORS.SQUID_INK,
                      }
                    : undefined,
                  prod.productCreditType && CREDIT_TYPE_TAG_CONFIG[prod.productCreditType]
                    ? {
                        copy: t(commonBundle, CREDIT_TYPE_TAG_CONFIG[prod.productCreditType].stringId),
                        icon: CREDIT_TYPE_TAG_CONFIG[prod.productCreditType].icon,
                        color: TAGLINE_COLORS.SQUID_INK,
                      }
                    : undefined,
                  prod.productCreditSegment && CREDIT_SEGMENT_TAG_CONFIG[prod.productCreditSegment]
                    ? {
                        copy: t(commonBundle, CREDIT_SEGMENT_TAG_CONFIG[prod.productCreditSegment].stringId),
                        icon: CREDIT_SEGMENT_TAG_CONFIG[prod.productCreditSegment].icon,
                        color: TAGLINE_COLORS.SQUID_INK,
                      }
                    : undefined,
                ].filter(Boolean) as Array<{ copy: string; icon?: any; color?: TAGLINE_COLORS }>,
              };
            })}
          />
        </PageSection>
      )}

      {/* Simplify how you buy credits */}

      <PageSection theme="squid-ink">
        <div
          ref={dynamicListSectionRef}
          className={classNames(styles.textComponent, (isFadingOut || hasFadedOut) && styles.textComponentFadeOut)}
        >
          <TextComponent
            isRebrandingActive
            theme={SECTION_THEME.Dark}
            heading={t(storeBundle, 'simplify_heading')}
            copy={t(storeBundle, 'simplify_subtitle')}
          />
        </div>
        <DynamicList
          className={styles.dynamicList}
          isRebrandingActive
          openNewTabString={t(commonBundle, 'newTab_label')}
          titleSpacing={DYNAMIC_LIST_TITLE_SPACING.REGULAR}
          subtitleSpacing={DYNAMIC_LIST_SUBTITLE_SPACING.LARGE}
          theme={SECTION_THEME.Dark}
          dynamicListScale={DYNAMIC_LIST_SVG_SCALE.LARGE}
          dynamicListSVG={StorefrontDynamicListSVG}
          svgProps={{
            svgAriaLabel: t(storeBundle, 'simplify_svg_ariaLabel'),
            heading: t(storeBundle, 'simplify_heading'),
            subtitle: t(storeBundle, 'simplify_subtitle'),
          }}
          items={[
            { title: t(storeBundle, 'simplify_item_1_title'), body: t(storeBundle, 'simplify_item_1_body') },
            { title: t(storeBundle, 'simplify_item_2_title'), body: t(storeBundle, 'simplify_item_2_body') },
            { title: t(storeBundle, 'simplify_item_3_title'), body: t(storeBundle, 'simplify_item_3_body') },
            { title: t(storeBundle, 'simplify_item_4_title'), body: t(storeBundle, 'simplify_item_4_body') },
            { title: t(storeBundle, 'simplify_item_5_title'), body: t(storeBundle, 'simplify_item_5_body') },
          ]}
        />
      </PageSection>
      {/* Steps for buying and retiring credits */}
      <PageSection theme="light-spruce">
        <MainBanner
          isRebrandingActive
          titleSize={HEADING_TAG.H2}
          theme={SECTION_THEME.Light}
          title={t(storeBundle, 'steps_title')}
          variant={SIMPLE_ACCORDION_VARIANT.INDEXED}
          accordions={[
            {
              title: t(storeBundle, 'steps_accordion_1_title'),
              content: [{ text: t(storeBundle, 'steps_accordion_1_text') }],
            },
            {
              title: t(storeBundle, 'steps_accordion_2_title'),
              content: [{ text: t(storeBundle, 'steps_accordion_2_text') }],
            },
            {
              title: t(storeBundle, 'steps_accordion_3_title'),
              content: [{ text: t(storeBundle, 'steps_accordion_3_text') }],
            },
            {
              title: t(storeBundle, 'steps_accordion_4_title'),
              content: [{ text: t(storeBundle, 'steps_accordion_4_text') }],
            },
          ]}
          image={{
            src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-authstorefront-stepstobuy.jpg`,
            alt: t(storeBundle, 'steps_image_alt'),
          }}
        />
      </PageSection>

      {/* Our carbon credit investments */}
      <PageSection theme="light-spruce">
        <Slider
          isRebrandingActive
          size={CARD_SIZE.M}
          theme={SECTION_THEME.Light}
          heading={t(storeBundle, 'investments_heading')}
          description={t(storeBundle, 'investments_description')}
          openNewTabLabel={t(commonBundle, 'newTab_label')}
          sliderPaginationLabel={t(commonBundle, 'of_label')}
          sliderNextAriaLabel={t(commonBundle, 'nextSlide_ariaLabel')}
          sliderPreviousAriaLabel={t(commonBundle, 'previousSlide_ariaLabel')}
        >
          <Card
            headingSize={HEADING_TAG.H4}
            title={t(storeBundle, 'investments_card_1_title')}
            subtitle={t(storeBundle, 'investments_card_1_subtitle')}
            icon="chevronRight"
            link={{
              label: isSmallDevice
                ? t(storeBundle, 'investments_card_explore_mobile')
                : t(storeBundle, 'investments_card_explore'),
              url: ROUTES.CREDITS_SEGMENT_SUPERPOLLUTANTS,
            }}
            image={{
              src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/segments-thumb-4.jpg`,
              alt: t(storeBundle, 'investments_card_1_img_alt'),
            }}
            openInSameTab
            forceServerSideNav
            openNewTabString={t(commonBundle, 'newTab_label')}
            cardAboutString={t(commonBundle, 'of_label')}
            isRebrandingActive
          />
          <Card
            headingSize={HEADING_TAG.H4}
            title={t(storeBundle, 'investments_card_2_title')}
            subtitle={t(storeBundle, 'investments_card_2_subtitle')}
            icon="chevronRight"
            link={{
              label: isSmallDevice
                ? t(storeBundle, 'investments_card_explore_mobile')
                : t(storeBundle, 'investments_card_explore'),
              url: ROUTES.CREDITS_SEGMENT_LOWER_CARBON_FUELS,
            }}
            image={{
              src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/segments-thumb-5.jpg`,
              alt: t(storeBundle, 'investments_card_2_img_alt'),
            }}
            openInSameTab
            forceServerSideNav
            openNewTabString={t(commonBundle, 'newTab_label')}
            cardAboutString={t(commonBundle, 'of_label')}
            isRebrandingActive
          />
          <Card
            headingSize={HEADING_TAG.H4}
            title={t(storeBundle, 'investments_card_3_title')}
            subtitle={t(storeBundle, 'investments_card_3_subtitle')}
            icon="chevronRight"
            link={{
              label: isSmallDevice
                ? t(storeBundle, 'investments_card_explore_mobile')
                : t(storeBundle, 'investments_card_explore'),
              url: ROUTES.CREDITS_SEGMENT_REDUCE_DEFORESTATION,
            }}
            image={{
              src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/segments-thumb-2.jpg`,
              alt: t(storeBundle, 'investments_card_3_img_alt'),
            }}
            openInSameTab
            forceServerSideNav
            openNewTabString={t(commonBundle, 'newTab_label')}
            cardAboutString={t(commonBundle, 'of_label')}
            isRebrandingActive
          />
          <Card
            headingSize={HEADING_TAG.H4}
            title={t(storeBundle, 'investments_card_4_title')}
            subtitle={t(storeBundle, 'investments_card_4_subtitle')}
            icon="chevronRight"
            link={{
              label: isSmallDevice
                ? t(storeBundle, 'investments_card_explore_mobile')
                : t(storeBundle, 'investments_card_explore'),
              url: ROUTES.CREDITS_SEGMENT_RESTORING_FORESTS,
            }}
            image={{
              src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/segments-thumb-1.jpg`,
              alt: t(storeBundle, 'investments_card_4_img_alt'),
            }}
            openInSameTab
            forceServerSideNav
            openNewTabString={t(commonBundle, 'newTab_label')}
            cardAboutString={t(commonBundle, 'of_label')}
            isRebrandingActive
          />
          <Card
            headingSize={HEADING_TAG.H4}
            title={t(storeBundle, 'investments_card_5_title')}
            subtitle={t(storeBundle, 'investments_card_5_subtitle')}
            icon="chevronRight"
            link={{
              label: isSmallDevice
                ? t(storeBundle, 'investments_card_explore_mobile')
                : t(storeBundle, 'investments_card_explore'),
              url: ROUTES.CREDITS_SEGMENT_TECH_CARBON_REMOVAL,
            }}
            image={{
              src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/segments-thumb-3.jpg`,
              alt: t(storeBundle, 'investments_card_5_img_alt'),
            }}
            openInSameTab
            forceServerSideNav
            openNewTabString={t(commonBundle, 'newTab_label')}
            cardAboutString={t(commonBundle, 'of_label')}
            isRebrandingActive
          />
        </Slider>
      </PageSection>

      {/* Additional resources */}
      <PageSection theme="squid-ink">
        <h2 className={styles.sectionTitle}>{t(storeBundle, 'resources_header')}</h2>
        <ListComponentMigrated
          hasMobileImages
          isRebrandingActive
          theme={SECTION_THEME.Dark}
          items={[
            {
              title: t(storeBundle, 'resources_item_4_title'),
              subtitle: t(storeBundle, 'resources_item_4_subtitle'),
              image: {
                src: `${CRL_DOMAIN}/credits/locale/en-us/resource-type/image/variation/desktop/credits-authstorefront-additionalresources-1.jpg`,
                alt: t(storeBundle, 'resources_item_4_img_alt'),
              },
              cta: {
                href: '/focus-areas/carbon-neutralization',
                type: CTA_TYPE_MIGRATED.Link,
                icon: ICON_NAME.CHEVRON_RIGHT,
                ariaLabelCopy: {
                  exploreText: t(commonBundle, 'newTab_label'),
                  newTabText: t(commonBundle, 'newTab_label'),
                  pdfText: '',
                },
              },
              label: t(storeBundle, 'resources_item_4_cta_label'),
              theme: SECTION_THEME.Dark,
              taglines: [{ copy: t(storeBundle, 'resources_item_4_tagline_1'), color: TAGLINE_COLORS.SQUID_INK }],
            },
            {
              title: t(storeBundle, 'resources_item_5_title'),
              subtitle: t(storeBundle, 'resources_item_5_subtitle'),
              image: {
                src: `${CRL_DOMAIN}/credits/locale/en-us/resource-type/image/variation/desktop/credits-authstorefront-additionalresources-2.jpg`,
                alt: t(storeBundle, 'resources_item_5_img_alt'),
              },
              cta: {
                href: '/focus-areas/carbon-insets',
                type: CTA_TYPE_MIGRATED.Link,
                icon: ICON_NAME.CHEVRON_RIGHT,
                ariaLabelCopy: {
                  exploreText: t(commonBundle, 'newTab_label'),
                  newTabText: t(commonBundle, 'newTab_label'),
                  pdfText: '',
                },
              },
              label: t(storeBundle, 'resources_item_5_cta_label'),
              theme: SECTION_THEME.Dark,
              taglines: [{ copy: t(storeBundle, 'resources_item_5_tagline_1'), color: TAGLINE_COLORS.SQUID_INK }],
            },
            {
              title: t(storeBundle, 'resources_item_6_title'),
              subtitle: t(storeBundle, 'resources_item_6_subtitle'),
              image: {
                src: `${CRL_DOMAIN}/credits/locale/en-us/resource-type/image/variation/desktop/credits-authstorefront-additionalresources-3.jpg`,
                alt: t(storeBundle, 'resources_item_6_img_alt'),
              },
              cta: {
                href: '/case-studies/corsairs-path-to-net-zero-carbon-electronics-manufacturing',
                type: CTA_TYPE_MIGRATED.Link,
                icon: ICON_NAME.CHEVRON_RIGHT,
                ariaLabelCopy: {
                  exploreText: t(commonBundle, 'newTab_label'),
                  newTabText: t(commonBundle, 'newTab_label'),
                  pdfText: '',
                },
              },
              label: t(storeBundle, 'resources_item_6_cta_label'),
              theme: SECTION_THEME.Dark,
              taglines: [{ copy: t(storeBundle, 'resources_item_6_tagline_1'), color: TAGLINE_COLORS.SQUID_INK }],
            },
          ]}
        />
      </PageSection>

      {/* Advocates */}
      <AdvocatesComponent
        scrollLabel={t(commonBundle, 'scrollDown_ariaLabel')}
        dataTrackingLabel="Advocates"
        theme={SECTION_THEME.Squid_Ink}
        isStandAlone
        brandTabs={{
          buttonsAriaLabels: {
            previous: t(commonBundle, 'previous_ariaLabel'),
            next: t(commonBundle, 'next_ariaLabel'),
          },
          options: [
            {
              brandIconImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-logo-3.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_krupp'),
              },
              profileImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-headshot-3.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_krupp'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_krupp'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_krupp'),
            },
            {
              brandIconImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-logo-2.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_figueres'),
              },
              profileImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-headshot-2.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_figueres'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_figueres'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_figueres'),
            },
            {
              brandIconImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-logo-1.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_sanjayan'),
              },
              profileImage: {
                src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-headshot-1.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_sanjayan'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_sanjayan'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_sanjayan'),
            },
          ],
        }}
        quotes={[
          t(segmentPageBundle, 'advocates_quote_krupp'),
          t(segmentPageBundle, 'advocates_quote_figueres'),
          t(segmentPageBundle, 'advocates_quote_sanjayan'),
        ]}
        backgroundImage={{
          src: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.IMAGE)}/credits-advocates-1.jpeg`,
          alt: t(segmentPageBundle, 'advocates_backgroundImage_altText'),
        }}
      />

      {/* Outro */}
      <Outro
        outroTitle={isSmallDesktop ? t(storeBundle, 'outro_title_desktop') : t(storeBundle, 'outro_title_mobile')}
        theme={SECTION_THEME.Squid_Ink}
        isRebrandingActive={isWeblabSegmentPagesEnabled}
        ctas={[
          {
            variant: PRIMARY_BUTTON_VARIANT.SecondaryNoIcon,
            label: t(storeBundle, 'cta_faqs'),
            url: ROUTES.CREDITS_FAQ,
            forceServerSideNav: true,
          },
          {
            label: t(storeBundle, 'cta_view_credits'),
            url: `${ROUTES.CREDITS_STORE}#available-credits`,
            forceServerSideNav: true,
          },
        ]}
        link={{
          label: t(segmentPageBundle, 'outro_link_californiaDisclosure_label'),
          url: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.PDF)}/California%20AB%201305%20Disclosure.pdf`,
        }}
      />
    </div>
  );
};
